import React from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Inbox, FileText, Users, BookOpen,
  FileSignature, Settings, Mail, LogOut, MessageSquare,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
  { to: "/admin/articles", label: "Articles", icon: FileText },
  { to: "/admin/pdf-generator", label: "Official PDF Generator", icon: FileSignature },
  { to: "/admin/issues", label: "Issues", icon: BookOpen },
  { to: "/admin/editorial-board", label: "Editorial Board", icon: Users },
  { to: "/admin/content", label: "Content (CMS)", icon: Settings },
  { to: "/admin/settings", label: "Journal Settings", icon: Settings },
  { to: "/admin/emails", label: "Email Logs", icon: Mail },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex" data-testid="admin-layout">
      <aside className="w-64 bg-[#001F3F] text-white flex-shrink-0 min-h-screen flex flex-col">
        <Link to="/" className="px-6 py-6 border-b border-white/10 flex items-center gap-3" data-testid="admin-sidebar-logo">
          <div className="flex items-center gap-3">
            <div className="bg-white text-[#001F3F] font-serif font-semibold text-lg w-10 h-12 flex items-center justify-center tracking-tight">
              CSR
            </div>
            <div>
              <div className="font-serif text-base leading-tight">Clinical &amp;</div>
              <div className="font-serif text-base leading-tight">Surgical Review</div>
            </div>
          </div>
        </Link>
        <nav className="flex-1 py-4">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-sans transition-colors ${
                  isActive ? "bg-[#2BA6A6] text-white" : "text-white/80 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/[ ()]/g, "-")}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="text-xs font-sans text-white/60 mb-1">Signed in as</div>
          <div className="text-sm font-sans text-white truncate" data-testid="admin-user-email">{user.email}</div>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-sans uppercase tracking-wider px-3 py-2 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-3 h-3" /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-8">
        <Outlet />
      </main>
    </div>
  );
}
